import { SplashScreen } from "@capacitor/splash-screen"
import { createInertiaApp } from "@inertiajs/react"
import "highlight.js/styles/github.css"
import { type ReactNode, createElement } from "react"
import { createRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"

// Hide splash when app is ready
const hideSplash = () => {
  // Wait for next frame to ensure content is painted
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      SplashScreen.hide({ fadeOutDuration: 300 }).catch(() => {
        // Silent fail on web
      })
    })
  })
}

// Temporary type definition, until @inertiajs/react provides one
interface ResolvedComponent {
  default: ReactNode & { layout?: (page: ReactNode) => ReactNode }
}

const appName = "CurrAr"

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: true,
    })
    const normalized = name.replace(/\.(tsx|ts)$/i, "")
    const candidates = [
      `../pages/${normalized}.tsx`,
      `../pages/${normalized}/index.tsx`,
    ]
    let page: ResolvedComponent | undefined
    for (const key of candidates) {
      if (key in pages) {
        page = pages[key]
        break
      }
    }
    if (!page) {
      // Case-insensitive fallback to avoid issues on different filesystems
      const lowerMap = new Map<string, string>()
      for (const key of Object.keys(pages)) lowerMap.set(key.toLowerCase(), key)
      for (const c of candidates) {
        const found = lowerMap.get(c.toLowerCase())
        if (found) {
          page = pages[found]
          break
        }
      }
    }
    if (!page) {
      console.error(`Missing Inertia page component: '${normalized}.tsx'`)
      console.warn("Available pages:", Object.keys(pages))
      throw new Error(`Inertia page not found: ${name}`)
    }

    page.default.layout ??= (pageNode) =>
      createElement(PersistentLayout, null, pageNode)

    return page
  },

  setup({ el, App, props }) {
    // Uncomment the following to enable SSR hydration:
    // if (el.hasChildNodes()) {
    //   hydrateRoot(el, createElement(App, props))
    //   return
    // }
    createRoot(el).render(createElement(App, props))

    // Hide splash after React has rendered
    hideSplash()
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker").catch(() => {
      // Fail silently if SW registration fails; app will still work as a normal web app.
    })
  })
}
